/**
 * Optimizer Control Center — the ⚡ "Update now" choice dialog. The manual run
 * is TWO independent steps the user picks freely: rebalance the flow weights,
 * and/or apply the queue rotation (eliminate/requeue per rules + promote from
 * the queue). Both are on by default — exactly what the scheduler does — and
 * the confirm button names what will run. Nothing selected = nothing to run.
 * A tracker without a queue (Tres Commas) only offers the weights step.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import { RefreshCw, Scale } from 'lucide-react';
import { trackerLabel } from '@trafficpulse/shared';

import { Checkbox } from '@/components/ui';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { queueProviderOf } from '@/features/trackers/flow-optimizer';

import { ConfirmActionDialog } from './ConfirmActionDialog';
import type { UpdateNowChoice } from './useRowActions';

export interface UpdateNowDialogProps {
  row: OptimizerRow;
  isBusy: boolean;
  onConfirm: (choice: UpdateNowChoice) => void;
  onCancel: () => void;
}

const ChoiceRow: FC<{
  id: string;
  checked: boolean;
  onChecked: (next: boolean) => void;
  Icon: typeof Scale;
  label: string;
  hint: string;
}> = ({ id, checked, onChecked, Icon, label, hint }) => (
  <label
    htmlFor={id}
    className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border bg-background/60 px-3 py-2 transition-colors hover:border-primary/40"
  >
    <Checkbox id={id} checked={checked} onCheckedChange={(c) => onChecked(c === true)} className="mt-0.5" />
    <span className="min-w-0">
      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        {label}
      </span>
      <span className="block text-[11px] leading-snug text-muted-foreground">{hint}</span>
    </span>
  </label>
);

const confirmLabelFor = ({ weights, rotation }: UpdateNowChoice): string =>
  weights && rotation ? 'Update weights + rotation' : weights ? 'Update weights' : 'Apply rotation';

export const UpdateNowDialog: FC<UpdateNowDialogProps> = ({
  row,
  isBusy,
  onConfirm,
  onCancel,
}) => {
  const canQueue = queueProviderOf(row.provider) !== null;
  const [weights, setWeights] = useState(true);
  const [rotation, setRotation] = useState(canQueue);
  const tracker = trackerLabel(row.provider);
  return (
    <ConfirmActionDialog
      title="Update now"
      confirmLabel={confirmLabelFor({ weights, rotation })}
      isBusy={isBusy}
      confirmDisabled={!weights && !rotation}
      onConfirm={() => onConfirm({ weights, rotation })}
      onCancel={onCancel}
      description={
        <div className="space-y-2">
          <p>
            Run for <span className="font-medium text-foreground">{row.campaignName}</span> now
            — changes push to {tracker} immediately. Pick what to run:
          </p>
          <ChoiceRow
            id="update-now-weights"
            checked={weights}
            onChecked={setWeights}
            Icon={Scale}
            label="Update flow weights"
            hint="Recalculate the formula and push the new weights."
          />
          {canQueue && (
            <ChoiceRow
              id="update-now-rotation"
              checked={rotation}
              onChecked={setRotation}
              Icon={RefreshCw}
              label="Apply queue rotation"
              hint="Eliminate / set to queue per the rules and promote the next queued elements."
            />
          )}
        </div>
      }
    />
  );
};
